import {useRef} from 'react';

import {
    Alert,
    Stack,
} from '@mantine/core';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';
import {DataTableColumn} from "mantine-datatable";

import {usePlaybooks} from '../hooks/usePlaybooks.ts';
import {usePlaybooksSelection} from '../hooks/usePlaybooksSelection.ts';
import {usePlaybooksSearch} from "../hooks/usePlaybooksSearch.ts";
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {rbacActions} from "../../../config/rbac/actions.ts";
import {Playbook} from '../types.ts';

// The `extraTools` / `settings` props were dropped with SimpleTableBatchAction
// (FNP-565): no caller passed either — IndexPage renders <RecordsTable/> bare —
// and TableSearchForm's contract is `handleFilterByKeyword` alone. Reinstating
// them would mean inventing a surface with no consumer.
export const RecordsTable = () => {

    const {allowedActions} = useVerifiedPermissions()

    const {
        columns,
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's match count is measured against.
        items,
        visibleItems: records,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchNextPage,
    } = usePlaybooks();

    // Same module-scoped store the list hook filters on — this component owns
    // the input, usePlaybooks owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = usePlaybooksSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection
    } = usePlaybooksSelection();

    // Checkboxes appear only for an operator who can actually delete. Offering
    // selection to someone with no action to apply to it is a dead affordance.
    let selectedRecords: Playbook[] = [];
    if (allowedActions.includes(rbacActions.DeletePlaybook)) {
        selectedRecords = records.filter(record => selection[record.id] === true)
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Two distinct empty states: nothing exists yet, versus nothing matched the
    // search. Collapsing them into one message tells an operator who mistyped a
    // keyword that they have no playbooks at all.
    const noRecordsText = keyword
        ? `No playbooks match "${keyword}" in the records loaded so far.`
        : 'No playbooks yet.';

    return (
        <Stack>
            {/* TableSearchForm, not SimpleTableBatchAction: the latter REQUIRES
              * `limit` and `handleItemsPerPageChange` in its type, and a
              * token-paginated table cannot honour a page-size change — there
              * is no page to resize and no total to divide. The call site ends
              * up passing a no-op handler, which is an affordance with nothing
              * behind it. */}
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title='Could not load playbooks'>
                    The workflows API did not return the list. Try Reload; if it persists the
                    API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    items.forEach((item: any) => {
                        handleRowSelection(item.id, true)
                    })
                }}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
            />

            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
                noun='playbook'
            />
        </Stack>
    );
};
